import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const isIOSDevice = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform?.toLowerCase() ?? "";

  return /iphone|ipad|ipod/.test(userAgent) ||
    (platform === "macintel" && window.navigator.maxTouchPoints > 1);
};

export function usePWAInstall() {
  const [isPWA, setIsPWA] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const checkPWA = () => {
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

      setIsPWA(standalone);
    };

    checkPWA();

    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const onDisplayModeChange = () => checkPWA();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", onDisplayModeChange);
    }

    const handler = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setInstallPrompt(installEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", onDisplayModeChange);
      }
    };
  }, []);

  const install = async () => {
    if (!installPrompt) {
      return false;
    }

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;

    if (choice.outcome === "accepted") {
      setInstallPrompt(null);
      return true;
    }

    return false;
  };

  return {
    isPWA,
    canInstall: !!installPrompt,
    isIOS: isIOSDevice(),
    install,
  };
}
